import {
  CHANNEL,
  type Channel,
  getChannelPerformanceLabel,
  getChannelPerformanceShortLabel,
} from 'src/data/channel';
import { GENERATION_TOOL, type GenerationTool } from 'src/data/generationTool';

import { getMetaGenderSelection } from './audiences';
import {
  AUDIENCE_FACET_KIND,
  type AudienceFacetKind,
  INTELLIGENCE_CHANNEL,
  INTELLIGENCE_CHANNELS,
  type IntelligenceChannel,
  LINKEDIN_OBJECTIVES,
  LINKEDIN_SOCIAL_OBJECTIVES,
  META_GENDER_SELECTION_LABEL,
  META_OBJECTIVES,
  PLAYBOOK_ASSET,
  PLAYBOOK_GENERATION_SOURCE_KIND,
  type PlaybookAsset,
  type PlaybookGenerationSourceKind,
  type PlaybookObjective,
  PLAYBOOKS_WIZARD_STEP,
  PLAYBOOKS_WIZARD_STEPS,
  type PlaybooksWizardStep,
} from './const';
import type { LinkedInFacets, MetaFacets, TargetAudienceFacets } from './types';

const EMPTY_FACET = '—';

const renderList = (values: string[]): string =>
  values.length > 0 ? values.join(', ') : EMPTY_FACET;

// The facets interface carries no field distinguishing its implementations — the owning audience's
// `channel` does. Callers that already selected a channel (a column set, a form's field body) hold
// that guarantee without being able to prove it from the union alone.
export const asLinkedinFacets = (facets: TargetAudienceFacets): LinkedInFacets =>
  facets as LinkedInFacets;

export const asMetaFacets = (facets: TargetAudienceFacets): MetaFacets => facets as MetaFacets;

const linkedinColumn = (
  key: string,
  label: string,
  render: (facets: LinkedInFacets) => string,
  minWidth?: number
): PlaybookFacetColumn => ({
  key,
  label,
  minWidth,
  render: (facets) => render(asLinkedinFacets(facets)),
});

const metaColumn = (
  key: string,
  label: string,
  render: (facets: MetaFacets) => string,
  minWidth?: number
): PlaybookFacetColumn => ({
  key,
  label,
  minWidth,
  render: (facets) => render(asMetaFacets(facets)),
});

export interface PlaybookFacetColumn {
  key: string;
  label: string;
  render: (facets: TargetAudienceFacets) => string;
  // Column width in px, for values a table would otherwise wrap mid-token. Carried as a number
  // rather than a class because the column set is data, and `src/data` owns no style modules.
  minWidth?: number;
}

// How a channel describes the people it targets. Both halves follow the vocabulary, not the
// channel. The columns render its facets, and an empty audience starts from its keys.
export interface PlaybookAudienceConfig {
  kind: AudienceFacetKind;
  facetColumns: PlaybookFacetColumn[];
  emptyFacets: TargetAudienceFacets;
}

export interface PlaybookAssetConfig {
  asset: PlaybookAsset;
  // The field a playbook ad carries this asset on.
  adField: 'headline' | 'introductoryText' | 'primaryText' | 'post';
}

// Everything that differs between the channels a playbook can target. There is no channel-specific
// LOGIC on this page — only this data — so components read the config instead of branching on the
// channel. Adding a channel is one entry here.
export interface PlaybookChannelConfig {
  channel: IntelligenceChannel;
  // The same platform as the integrations enum names it — Meta Ads is `facebook` there. Only the
  // handful of calls that reach integrations (connect, ad accounts, ad copies) use this.
  integrationChannel: Channel;
  // e.g. "Meta ads" — reads naturally mid-sentence.
  channelLabel: string;
  // e.g. "Meta" — for labels the channel already qualifies, like "Meta objective".
  channelShortLabel: string;
  // e.g. "Meta Ads Performance Insights" — page and document title.
  pageTitle: string;
  // e.g. "retargeting ads or lead magnet promotion" — the welcome screen's examples of a
  // playbook on this channel.
  welcomeExamples: string;
  generationSourceKind: PlaybookGenerationSourceKind;
  // Which identifier the backend wants for a connected ad account. Meta predates the integration
  // asset model, so the backend resolves it from the account id; LinkedIn goes through the asset.
  generationSourceAccountField: 'id' | 'integrationAssetId';
  connectContextLabel: string;
  contextDescription: string;
  // How a run's coverage reads on screen. A `copies` channel reports `coverage.uniqueCopies` under
  // the item noun. That count is not `coverage.items`, and the two disagree on the same run.
  coverageSummary: 'hierarchy' | 'copies';
  // What the channel calls the two units a run's coverage counts. The backend reports them
  // channel-neutrally as `coverage.blocks` and `coverage.items`. A block groups the items. An ad
  // account groups delivered ads under ad groups. A page groups published posts under post types.
  blockLabel: string;
  blockLabelPlural: string;
  itemLabel: string;
  itemLabelPlural: string;
  // The full name of the account a run reads from. The channel's own name is part of it, so a
  // caller does not add it.
  accountLabel: string;
  objectives: PlaybookObjective[];
  // The steps the Setup playbooks wizard walks for this channel.
  wizardSteps: PlaybooksWizardStep[];
  // In the order the channel's ad shows them: the long-form text above the headline. An organic
  // post is one asset, so the list is not always a pair.
  assets: PlaybookAssetConfig[];
  // Absent on a channel that targets nobody. An organic page reaches the people who already
  // follow it. It has no audience to describe and no step that builds one.
  audiences?: PlaybookAudienceConfig;
  templateTool: GenerationTool;
  // Whether staff can upload a file of the client's own A/B tests as a run source. The backend reads
  // the file's Title and Body into an ad's two assets, so a channel of one-asset posts takes no file.
  acceptsAbTestUpload: boolean;
}

const LINKEDIN_FACET_COLUMNS: PlaybookFacetColumn[] = [
  linkedinColumn('locations', 'Locations', (facets) => renderList(facets.locations)),
  linkedinColumn('jobTitles', 'Job titles', (facets) => renderList(facets.jobTitles)),
  linkedinColumn('seniorities', 'Seniorities', (facets) => renderList(facets.seniorities)),
  linkedinColumn('industries', 'Industries', (facets) => renderList(facets.industries)),
  linkedinColumn('companySizes', 'Company sizes', (facets) => renderList(facets.companySizes)),
];

const renderAgeRange = (facets: MetaFacets): string =>
  facets.ageRange ? `${facets.ageRange.minAge}–${facets.ageRange.maxAge}` : EMPTY_FACET;

// The stored facet holds the platform's own values, so it reads back through the form's three-way
// choice — an empty list is everyone, which shows as "All" rather than blank.
const renderGenders = (facets: MetaFacets): string =>
  META_GENDER_SELECTION_LABEL[getMetaGenderSelection(facets.genders)];

const META_FACET_COLUMNS: PlaybookFacetColumn[] = [
  metaColumn('locations', 'Locations', (facets) => renderList(facets.locations), 120),
  // An age range is one token to a reader but two to the layout engine, which happily breaks it at
  // the dash; this is the width that keeps the widest range on one line.
  metaColumn('age', 'Age', renderAgeRange, 78),
  metaColumn('genders', 'Gender', renderGenders),
  metaColumn('languages', 'Languages', (facets) => renderList(facets.languages)),
  // The two lists read as one idea on screen, so they share a column.
  metaColumn('interests', 'Interests / Behaviors', (facets) =>
    renderList([...facets.interests, ...facets.behaviors])
  ),
];

// A channel that targets no audience never stops at the audiences step.
const SOCIAL_WIZARD_STEPS: PlaybooksWizardStep[] = PLAYBOOKS_WIZARD_STEPS.filter(
  (step) => step !== PLAYBOOKS_WIZARD_STEP.audiences
);

export const EMPTY_LINKEDIN_FACETS: LinkedInFacets = {
  locations: [],
  jobTitles: [],
  seniorities: [],
  industries: [],
  companySizes: [],
};

export const EMPTY_META_FACETS: MetaFacets = {
  locations: [],
  ageRange: null,
  genders: [],
  languages: [],
  interests: [],
  behaviors: [],
};

const LINKEDIN_AUDIENCE_CONFIG: PlaybookAudienceConfig = {
  kind: AUDIENCE_FACET_KIND.linkedin,
  facetColumns: LINKEDIN_FACET_COLUMNS,
  emptyFacets: EMPTY_LINKEDIN_FACETS,
};

const META_AUDIENCE_CONFIG: PlaybookAudienceConfig = {
  kind: AUDIENCE_FACET_KIND.meta,
  facetColumns: META_FACET_COLUMNS,
  emptyFacets: EMPTY_META_FACETS,
};

export const PLAYBOOK_CHANNEL_CONFIG: Record<IntelligenceChannel, PlaybookChannelConfig> = {
  [INTELLIGENCE_CHANNEL.linkedinAds]: {
    channel: INTELLIGENCE_CHANNEL.linkedinAds,
    integrationChannel: CHANNEL.linkedin,
    channelLabel: getChannelPerformanceLabel(CHANNEL.linkedin),
    channelShortLabel: getChannelPerformanceShortLabel(CHANNEL.linkedin),
    pageTitle: 'LinkedIn Ads Performance Insights',
    welcomeExamples: 'retargeting ads or lead magnet promotion',
    generationSourceKind: PLAYBOOK_GENERATION_SOURCE_KIND.linkedinAds,
    generationSourceAccountField: 'integrationAssetId',
    connectContextLabel: 'Connect to LinkedIn ad account',
    contextDescription: 'Connect your ad account, share your website or upload files.',
    coverageSummary: 'hierarchy',
    blockLabel: 'campaign',
    blockLabelPlural: 'campaigns',
    itemLabel: 'ad',
    itemLabelPlural: 'ads',
    accountLabel: 'LinkedIn ad account',
    objectives: LINKEDIN_OBJECTIVES,
    wizardSteps: PLAYBOOKS_WIZARD_STEPS,
    assets: [
      { asset: PLAYBOOK_ASSET.introductoryText, adField: 'introductoryText' },
      { asset: PLAYBOOK_ASSET.headline, adField: 'headline' },
    ],
    audiences: LINKEDIN_AUDIENCE_CONFIG,
    templateTool: GENERATION_TOOL.linkedinMixtureAd,
    acceptsAbTestUpload: true,
  },
  [INTELLIGENCE_CHANNEL.metaAds]: {
    channel: INTELLIGENCE_CHANNEL.metaAds,
    integrationChannel: CHANNEL.facebook,
    channelLabel: getChannelPerformanceLabel(CHANNEL.facebook),
    channelShortLabel: getChannelPerformanceShortLabel(CHANNEL.facebook),
    pageTitle: 'Meta Ads Performance Insights',
    welcomeExamples: 'retargeting ads or lead magnet promotion',
    generationSourceKind: PLAYBOOK_GENERATION_SOURCE_KIND.metaAds,
    generationSourceAccountField: 'id',
    connectContextLabel: 'Connect Meta ad account',
    contextDescription: 'Connect your ad account, share your website or upload files.',
    coverageSummary: 'hierarchy',
    blockLabel: 'ad set',
    blockLabelPlural: 'ad sets',
    itemLabel: 'ad',
    itemLabelPlural: 'ads',
    accountLabel: 'Meta ad account',
    objectives: META_OBJECTIVES,
    wizardSteps: PLAYBOOKS_WIZARD_STEPS,
    assets: [
      { asset: PLAYBOOK_ASSET.primaryText, adField: 'primaryText' },
      { asset: PLAYBOOK_ASSET.headline, adField: 'headline' },
    ],
    audiences: META_AUDIENCE_CONFIG,
    templateTool: GENERATION_TOOL.facebookMixtureAd,
    acceptsAbTestUpload: true,
  },
  [INTELLIGENCE_CHANNEL.linkedinSocial]: {
    channel: INTELLIGENCE_CHANNEL.linkedinSocial,
    integrationChannel: CHANNEL.linkedinSocial,
    channelLabel: getChannelPerformanceLabel(CHANNEL.linkedinSocial),
    channelShortLabel: getChannelPerformanceShortLabel(CHANNEL.linkedinSocial),
    pageTitle: 'LinkedIn Performance Insights',
    welcomeExamples: 'thought leadership or customer stories',
    generationSourceKind: PLAYBOOK_GENERATION_SOURCE_KIND.linkedinSocial,
    generationSourceAccountField: 'integrationAssetId',
    connectContextLabel: 'Connect to LinkedIn',
    contextDescription: 'Connect your page, share your website or upload files.',
    coverageSummary: 'copies',
    // A page has no campaigns and no ad sets. The backend groups its posts by post type instead.
    blockLabel: 'post type',
    blockLabelPlural: 'post types',
    itemLabel: 'post',
    itemLabelPlural: 'posts',
    accountLabel: 'LinkedIn',
    objectives: LINKEDIN_SOCIAL_OBJECTIVES,
    wizardSteps: SOCIAL_WIZARD_STEPS,
    assets: [{ asset: PLAYBOOK_ASSET.post, adField: 'post' }],
    templateTool: GENERATION_TOOL.linkedinPost,
    acceptsAbTestUpload: false,
  },
};

// Integrations-side helpers. The feature speaks IntelligenceChannel everywhere; these are the only
// sanctioned way out to the integrations vocabulary, so a wrong conversion has one place to look.
export const INTELLIGENCE_INTEGRATION_CHANNELS: Channel[] = INTELLIGENCE_CHANNELS.map(
  (channel) => PLAYBOOK_CHANNEL_CONFIG[channel].integrationChannel
);

export const toIntegrationChannel = (channel: IntelligenceChannel): Channel =>
  PLAYBOOK_CHANNEL_CONFIG[channel].integrationChannel;

// Undefined for a platform the intelligence features do not cover, so callers holding an arbitrary
// integrations channel can ask without asserting.
export const fromIntegrationChannel = (channel?: string): IntelligenceChannel | undefined =>
  INTELLIGENCE_CHANNELS.find(
    (candidate) => PLAYBOOK_CHANNEL_CONFIG[candidate].integrationChannel === channel
  );

export const isIntelligenceChannel = (channel?: string): channel is IntelligenceChannel =>
  !!channel && channel in PLAYBOOK_CHANNEL_CONFIG;

export const getPlaybookChannelConfig = (channel: IntelligenceChannel): PlaybookChannelConfig =>
  PLAYBOOK_CHANNEL_CONFIG[channel];

// Narrows a stored or routed value to a supported channel. The page validates its URL channel
// before mounting any store, so reaching this with an unsupported one means the store was used
// outside that guarantee.
export const requireIntelligenceChannel = (channel?: string | null): IntelligenceChannel =>
  requirePlaybookChannelConfig(channel).channel;

// The wizard offers the audiences step only on a channel that has one. An audience screen that
// reaches this mounted outside that gate.
export const requirePlaybookAudienceConfig = (
  config: PlaybookChannelConfig
): PlaybookAudienceConfig => {
  if (!config.audiences) {
    throw new Error(`Channel targets no audience: ${config.channel}`);
  }
  return config.audiences;
};

// The page validates its URL channel before mounting any store, so reaching this with an
// unsupported channel means the store was used outside that guarantee.
export const requirePlaybookChannelConfig = (channel?: string | null): PlaybookChannelConfig => {
  const candidate = channel ?? undefined;
  if (!isIntelligenceChannel(candidate)) {
    throw new Error(`Unsupported performance insights channel: ${channel}`);
  }
  return PLAYBOOK_CHANNEL_CONFIG[candidate];
};
